import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootEntity } from "@types-runway/state";

export type EntityError = RootEntity<unknown>["error"];

// One `RootEntity<T>` slice per piece of API data the rebuilt admin holds.
// The reducer names follow the house convention (pending / fetchSuccess /
// mutationSuccess / error / clear), namespaced by `name` so the entities of
// one job never answer each other's actions.
export const createEntitySlice = <T>(name: string, emptyData: T) => {
  const initialState: RootEntity<T> = {
    isPending: false,
    success: { type: null },
    data: emptyData,
    error: {},
  };

  return createSlice({
    name,
    initialState,
    reducers: {
      // Keeps the current data on screen while the request runs (XCUT-R1 shows
      // the pending state on the control, not by blanking the results).
      pending: (state) => ({ ...state, isPending: true, success: { type: null }, error: {} }),
      fetchSuccess: (state, action: PayloadAction<T>) => ({
        ...state,
        isPending: false,
        success: { type: "read" },
        data: action.payload,
        error: {},
      }),
      mutationSuccess: (state, action: PayloadAction<T>) => ({
        ...state,
        isPending: false,
        success: { type: "write" },
        data: action.payload,
        error: {},
      }),
      // A failed request leaves no partially-updated results behind (XCUT-R3):
      // the data returns to empty and only the error remains to be shown.
      error: (state, action: PayloadAction<EntityError>) => ({
        ...state,
        isPending: false,
        success: { type: null },
        data: emptyData,
        error: action.payload,
      }),
      clear: () => initialState,
    },
  });
};
